import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import ValueBadge from '../components/ValueBadge';
import PointsBar from '../components/PointsBar';
import septemberContext from '../septemberContext';
import VibeTags from '../components/VibeTags';
import DestImage from '../components/DestImage';
import AnimatedCounter from '../components/AnimatedCounter';
import { useTripBuilder } from '../TripBuilderContext';

const sortOptions = [
  { value: 'value', label: 'Best Points Value' },
  { value: 'remaining', label: 'Most Points Leftover' },
  { value: 'nonstop', label: 'Nonstop Flights First' },
  { value: 'shortest', label: 'Shortest Flight' },
  { value: 'cheapest', label: 'Lowest Cash Spend' },
  { value: 'default', label: 'Our Picks' },
];

const gradientOverlays = [
  'from-orange-900/80 via-rose-900/60 to-transparent',
  'from-blue-900/80 via-cyan-900/60 to-transparent',
  'from-amber-900/80 via-orange-900/60 to-transparent',
  'from-emerald-900/80 via-teal-900/60 to-transparent',
  'from-red-900/80 via-pink-900/60 to-transparent',
  'from-violet-900/80 via-purple-900/60 to-transparent',
];

const accentColors = [
  'border-orange-500/30 hover:border-orange-400/50',
  'border-blue-500/30 hover:border-blue-400/50',
  'border-amber-500/30 hover:border-amber-400/50',
  'border-emerald-500/30 hover:border-emerald-400/50',
  'border-red-500/30 hover:border-red-400/50',
  'border-violet-500/30 hover:border-violet-400/50',
];

const CABIN_LABEL = {
  economy: 'Economy',
  premium: 'Premium Economy',
  business: 'Business Class',
  first: 'First Class',
};

export default function CommandCenter({ compareIds, toggleCompare }) {
  const { amexTotal: amex, chaseTotal: chase, userConfig } = useTripBuilder();
  const programCount = Object.values(userConfig?.pointsBalances || {}).filter(v => Number(v) > 0).length;
  const cabinLabel = CABIN_LABEL[userConfig?.cabinClass] || 'Business Class';
  const [destinations, setDestinations] = useState([]);
  const [sortBy, setSortBy] = useState('default');
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDest, setSelectedDest] = useState(null);

  useEffect(() => {
    Promise.all([api.getDestinations(), api.getTransferBonuses()])
      .then(([dests, bons]) => { setDestinations(dests); setBonuses(bons.filter(b => b.is_active)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleFavorite = async (id) => {
    await api.toggleFavorite(id);
    setDestinations(prev => prev.map(d => d.id === id ? { ...d, is_favorite: !d.is_favorite } : d));
  };

  const sorted = [...destinations].sort((a, b) => {
    const aRec = a.best_redemption?.[0];
    const bRec = b.best_redemption?.[0];
    if (sortBy === 'value') return (bRec?.cents_per_point || 0) - (aRec?.cents_per_point || 0);
    if (sortBy === 'remaining') return ((amex - (aRec?.total_points_two_travelers || amex)) - (amex - (bRec?.total_points_two_travelers || amex)));
    if (sortBy === 'cheapest') return (a.avg_daily_budget_min || 0) - (b.avg_daily_budget_min || 0);
    if (sortBy === 'nonstop') return (bRec?.is_nonstop ? 1 : 0) - (aRec?.is_nonstop ? 1 : 0);
    if (sortBy === 'shortest') return (aRec?.flight_duration_outbound || 'zzz').localeCompare(bRec?.flight_duration_outbound || 'zzz');
    return a.sort_order - b.sort_order;
  });

  const total = amex + chase;

  if (loading) {
    return (
      <div className="space-y-8 pb-16">
        <div className="rounded-2xl p-8 border border-brand-ink/10 space-y-6">
          <div className="text-center space-y-3">
            <div className="animate-shimmer h-4 w-40 mx-auto rounded" />
            <div className="animate-shimmer h-14 w-64 mx-auto rounded-lg" />
            <div className="animate-shimmer h-4 w-56 mx-auto rounded" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <div className="animate-shimmer h-28 rounded-xl" />
            <div className="animate-shimmer h-28 rounded-xl" style={{ animationDelay: '200ms' }} />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[0,1,2,3,4,5].map(i => (
            <div key={i} className="rounded-2xl border border-brand-ink/10 overflow-hidden" style={{ animationDelay: `${i * 100}ms` }}>
              <div className="animate-shimmer h-44" />
              <div className="p-4 space-y-3">
                <div className="animate-shimmer h-4 w-3/4 rounded" />
                <div className="animate-shimmer h-16 rounded-lg" />
                <div className="animate-shimmer h-3 w-1/2 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-16">
      {/* Points Arsenal */}
      <section>
        <div className="animate-fade-in-up relative bg-gradient-to-br from-navy-light via-navy to-navy-lighter rounded-2xl p-8 border border-brand-ink/10 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl" />
          <div className="relative text-center mb-8">
            <div className="text-brand-ink/65 text-xs font-bold uppercase tracking-[0.2em] mb-2">YOUR POINTS ARSENAL</div>
            <div className="text-6xl font-black bg-gradient-to-r from-teal via-emerald-400 to-teal bg-clip-text text-transparent"><AnimatedCounter value={total} duration={1400} /></div>
            <div className="text-brand-ink/65 text-sm mt-2">Total Points &middot; {programCount || 0} Program{programCount === 1 ? '' : 's'} &middot; {cabinLabel}</div>
          </div>
          <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <div className="bg-blue-900/30 rounded-xl p-5 border border-blue-500/20 hover:border-blue-400/40 transition">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-blue-300 uppercase tracking-wider">Amex MR</span>
                <span className="text-[10px] text-brand-ink/55">17 airline + 3 hotel partners</span>
              </div>
              <div className="text-3xl font-black"><AnimatedCounter value={amex} duration={1000} /></div>
              <div className="text-xs text-blue-400/60 mt-1 font-medium">Use for flights</div>
            </div>
            <div className="bg-indigo-900/30 rounded-xl p-5 border border-indigo-500/20 hover:border-indigo-400/40 transition">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-indigo-300 uppercase tracking-wider">Chase UR</span>
                <span className="text-[10px] text-brand-ink/55">12 airline + 3 hotel partners</span>
              </div>
              <div className="text-3xl font-black"><AnimatedCounter value={chase} duration={1000} /></div>
              <div className="text-xs text-indigo-400/60 mt-1 font-medium">Use for hotels (Hyatt)</div>
            </div>
          </div>
        </div>

        <div className="mt-4 bg-teal/10 border border-teal/30 rounded-xl p-4 flex items-start gap-3">
          <span className="text-teal text-lg mt-0.5">&#9733;</span>
          <div>
            <div className="font-semibold text-teal text-sm">STRATEGY</div>
            <div className="text-sm text-brand-ink/80 mt-1">
              <strong>Amex MR for flights</strong> (transfer to Aeroplan, LifeMiles, Iberia Avios, etc.) +{' '}
              <strong>Chase UR for hotels</strong> (transfer to World of Hyatt — Chase exclusive, best hotel value at 2+ cpp).
              Both programs transfer to Aeroplan 1:1 if you need to pool for a big redemption.
            </div>
          </div>
        </div>

      </section>

      {/* Transfer Warnings */}
      <section className="bg-gold/10 border border-gold/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <span className="text-gold text-lg">&#9888;</span>
          <div className="text-sm">
            <div className="font-semibold text-gold mb-1">TRANSFER RULES — READ BEFORE YOU MOVE POINTS</div>
            <div className="text-brand-ink/75 space-y-1">
              <div>&#8226; Transfers are <strong className="text-brand-ink/85">ONE-WAY and IRREVERSIBLE</strong>. Once transferred, points cannot be moved back.</div>
              <div>&#8226; <strong className="text-brand-ink/85">NEVER transfer before confirming availability.</strong> Search first, transfer second.</div>
              <div>&#8226; ANA transfers take 2-3 business days. All others from Amex and Chase are instant.</div>
            </div>
            <Link to="/transfers" className="text-gold text-xs font-medium mt-2 inline-block hover:underline">View all transfer rules and partners &rarr;</Link>
          </div>
        </div>
      </section>

      {/* Active Bonuses */}
      {bonuses.length > 0 && (
        <section>
          <div className="bg-green-900/20 border border-teal/30 rounded-xl p-4">
            <div className="text-sm font-semibold text-teal mb-2">ACTIVE TRANSFER BONUSES</div>
            {bonuses.map(b => (
              <div key={b.id} className="flex items-center gap-3 text-sm">
                <span className="px-2 py-0.5 bg-teal/20 text-teal rounded text-xs font-bold">+{b.bonus_percentage}%</span>
                <span>{b.source_program === 'amex_mr' ? 'Amex' : 'Chase'} &rarr; {b.airline_program_short}</span>
                {b.end_date && <span className="text-brand-ink/65 text-xs">ends {new Date(b.end_date).toLocaleDateString()}</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Destination Showdown */}
      <section>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-bold">Top Picks</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-brand-ink/65">Sort by:</span>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="bg-navy-lighter border border-brand-ink/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-teal"
            >
              {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {sorted.map((dest, i) => {
            const rec = dest.best_redemption?.[0];
            const hotel = dest.best_hotel?.[0];
            const amexUsed = rec?.total_points_two_travelers || 0;
            const chaseUsed = hotel?.total_points || 0;

            return (
              <div key={dest.id}
                onClick={() => setSelectedDest(dest)}
                className={`animate-card-in bg-navy-light rounded-2xl border ${accentColors[i % accentColors.length]} overflow-hidden transition-all duration-300 group hover:shadow-lg hover:shadow-black/20 hover:-translate-y-1 cursor-pointer`}
                style={{ animationDelay: `${i * 80}ms` }}>
                {/* Hero with real image (or branded fallback if missing) */}
                <div className="h-44 relative overflow-hidden">
                  <DestImage city={dest.city} imgClassName="group-hover:scale-105 transition-transform duration-500" />
                  <div className={`absolute inset-0 bg-gradient-to-t ${gradientOverlays[i % gradientOverlays.length]}`} />
                  {rec?.cents_per_point >= 3 && (
                    <div className="absolute top-3 left-3 bg-teal text-navy text-xs font-extrabold px-2.5 py-1 rounded-full shadow-lg">
                      BEST VALUE
                    </div>
                  )}
                  {rec?.is_nonstop && (
                    <div className="absolute top-3 left-3 mt-7 bg-brand-ink/20 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      NONSTOP
                    </div>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleFavorite(dest.id); }}
                    className="absolute top-3 right-3 text-2xl transition hover:scale-125 drop-shadow-lg"
                  >
                    {dest.is_favorite ? '\u2764\uFE0F' : '\uD83E\uDD0D'}
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
                    <div className="text-2xl font-extrabold drop-shadow-lg tracking-tight">{dest.city}</div>
                    <div className="text-sm text-brand-ink/90 font-medium">{dest.country} &middot; {dest.weather_high_f}°F in Sep</div>
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  <VibeTags tags={dest.vibe_tags} size="xs" />

                  {rec && (
                    <div className="bg-gradient-to-r from-white/[0.06] to-white/[0.02] rounded-lg p-3 space-y-2 border border-brand-ink/5">
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-wider text-brand-ink/65 font-semibold">THE PLAY</div>
                        <ValueBadge cpp={parseFloat(rec.cents_per_point)} size="sm" />
                      </div>
                      <div className="text-sm font-medium text-brand-ink/90">
                        {amexUsed.toLocaleString()} pts &rarr; {rec.airline_program}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap text-xs">
                        <span className="text-brand-ink/70">{rec.transfer_time}</span>
                        {rec.flight_duration_outbound && (
                          <><span className="text-brand-ink/45">&middot;</span><span className="text-brand-ink/70">{rec.flight_duration_outbound}</span></>
                        )}
                        {rec.num_stops > 0 && !rec.is_nonstop && (
                          <><span className="text-brand-ink/45">&middot;</span><span className="text-brand-ink/65">{rec.num_stops} stop</span></>
                        )}
                      </div>
                    </div>
                  )}

                  {septemberContext[dest.city] && (
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className={`px-1.5 py-0.5 rounded-full font-bold ${
                        septemberContext[dest.city].crowdLevel === 'Very Low' || septemberContext[dest.city].crowdLevel === 'Low' ? 'bg-teal/15 text-teal' :
                        septemberContext[dest.city].crowdLevel === 'Low-Medium' ? 'bg-teal/10 text-teal/70' : 'bg-gold/15 text-gold'
                      }`}>{septemberContext[dest.city].crowdLevel} crowds</span>
                      <span className="text-brand-ink/55 truncate">{septemberContext[dest.city].inSeason[0]}</span>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <PointsBar used={amexUsed} total={amex} label="Amex MR (flights)" color="bg-blue-500" />
                    {hotel && (
                      <PointsBar used={chaseUsed} total={chase} label="Chase UR (hotels)" color="bg-indigo-500" />
                    )}
                  </div>

                  <div className="flex items-center justify-between text-xs text-brand-ink/65">
                    <span>Taxes ~${rec?.total_estimated_taxes?.toLocaleString() || '?'}</span>
                    <span>${dest.avg_daily_budget_min}-{dest.avg_daily_budget_max}/day</span>
                  </div>

                  <div className="flex gap-2 pt-1">
                    <Link
                      to={`/destination/${dest.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 text-center px-3 py-2.5 bg-gradient-to-r from-teal to-teal-dark text-navy rounded-lg text-sm font-bold hover:opacity-90 transition shadow-md shadow-teal/20"
                    >
                      Explore &rarr;
                    </Link>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleCompare(dest.id); }}
                      className={`px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                        compareIds.includes(dest.id)
                          ? 'bg-gold/20 text-gold ring-1 ring-gold/30'
                          : 'bg-brand-ink/5 text-brand-ink/70 hover:bg-brand-ink/10'
                      }`}
                    >
                      {compareIds.includes(dest.id) ? 'Added' : 'Compare'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* No Active Bonuses Fallback */}
      {bonuses.length === 0 && (
        <section className="bg-navy-light rounded-xl p-4 border border-brand-ink/10">
          <div className="text-sm font-semibold text-brand-ink/65 mb-1">TRANSFER BONUSES</div>
          <div className="text-sm text-brand-ink/55">No active transfer bonuses right now. Bonuses typically run 3-4x per year per partner. Check back or ask the Advisor for historical patterns.</div>
        </section>
      )}

      {/* Destination Detail Modal */}
      {selectedDest && (
        <DestinationModal
          dest={selectedDest}
          onClose={() => setSelectedDest(null)}
          amex={amex}
          chase={chase}
          toggleCompare={toggleCompare}
          compareIds={compareIds}
        />
      )}
    </div>
  );
}

function DestinationModal({ dest, onClose, amex, chase, toggleCompare, compareIds }) {
  const navigate = useNavigate();
  const rec = dest.best_redemption?.[0];
  const hotel = dest.best_hotel?.[0];
  const ctx = septemberContext[dest.city];

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative bg-navy-light border border-brand-ink/10 rounded-2xl max-w-xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}>

        {/* Close button */}
        <button onClick={onClose}
          className="absolute top-3 right-3 text-white hover:text-white text-2xl z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 transition shadow-lg">
          &times;
        </button>

        {/* Hero Image */}
        <div className="h-48 relative overflow-hidden rounded-t-2xl">
          <DestImage city={dest.city} />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-light via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h2 className="text-2xl font-extrabold drop-shadow-lg">{dest.city}</h2>
            <div className="text-sm text-brand-ink/85">{dest.country} &middot; {dest.region}</div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Vibes */}
          <VibeTags tags={dest.vibe_tags} />

          {/* Description */}
          <p className="text-sm text-brand-ink/80 leading-relaxed">{dest.description}</p>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-brand-ink/5 rounded-xl p-3 text-center">
              <div className="text-lg font-bold">{dest.weather_high_f}°F</div>
              <div className="text-[10px] text-brand-ink/65">Sep Weather</div>
            </div>
            <div className="bg-brand-ink/5 rounded-xl p-3 text-center">
              <div className="text-lg font-bold">${dest.avg_daily_budget_min}-{dest.avg_daily_budget_max}</div>
              <div className="text-[10px] text-brand-ink/65">Daily Budget</div>
            </div>
            <div className="bg-brand-ink/5 rounded-xl p-3 text-center">
              <div className="text-lg font-bold">{dest.weather_rain_chance}</div>
              <div className="text-[10px] text-brand-ink/65">Rain Chance</div>
            </div>
          </div>

          {/* September Intel */}
          {ctx && (
            <div className="bg-gradient-to-r from-teal/10 to-indigo-500/10 border border-teal/20 rounded-xl p-4 space-y-2">
              <div className="text-xs font-bold text-teal uppercase">September Intel</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-brand-ink/65">Weather:</span> <span className="text-brand-ink/85">{ctx.weatherVibe}, {ctx.temp}</span></div>
                <div><span className="text-brand-ink/65">Crowds:</span> <span className={`font-medium ${ctx.crowdLevel === 'Very Low' || ctx.crowdLevel === 'Low' ? 'text-teal' : 'text-gold'}`}>{ctx.crowdLevel}</span></div>
              </div>
              <div className="text-xs text-brand-ink/75">{ctx.crowdNote}</div>
              <div className="text-xs text-brand-ink/70">
                <span className="text-brand-ink/65">In season:</span> {ctx.inSeason.join(' / ')}
              </div>
              {ctx.warning && (
                <div className="text-xs text-gold bg-gold/10 rounded-lg p-2 mt-1">
                  Heads up: {ctx.warning}
                </div>
              )}
              <div className="text-xs text-teal/70 italic">{ctx.bestFor}</div>
            </div>
          )}

          {/* Best Flight */}
          {rec && (
            <div className="bg-brand-ink/5 rounded-xl p-4 space-y-2 border border-brand-ink/5">
              <div className="text-xs font-bold text-brand-ink/65 uppercase">Best Flight Option</div>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">{rec.routing_description}</div>
                <ValueBadge cpp={parseFloat(rec.cents_per_point)} />
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-brand-ink/70">
                <span className="px-2 py-0.5 bg-blue-900/50 rounded text-blue-300 font-medium">Amex MR &rarr; {rec.airline_program}</span>
                <span>{rec.flight_duration_outbound}</span>
                {rec.is_nonstop && <span className="text-teal font-bold">NONSTOP</span>}
                {!rec.is_nonstop && <span>{rec.num_stops} stop{rec.num_stops > 1 ? 's' : ''}</span>}
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="font-bold">{rec.total_points_two_travelers?.toLocaleString()} pts</span>
                <span className="text-brand-ink/55">+</span>
                <span className="text-brand-ink/75">${rec.total_estimated_taxes} taxes</span>
              </div>
              <PointsBar used={rec.total_points_two_travelers} total={amex} label="Amex MR" color="bg-blue-500" />
            </div>
          )}

          {/* Best Hotel */}
          {hotel && (
            <div className="bg-brand-ink/5 rounded-xl p-4 space-y-2 border border-brand-ink/5">
              <div className="text-xs font-bold text-brand-ink/65 uppercase">Best Hotel Option</div>
              <div className="text-sm font-medium">{hotel.hotel_name}</div>
              <div className="text-xs text-brand-ink/70">via {hotel.hotel_program} &middot; {hotel.nights} nights</div>
              <div className="flex items-center gap-3 text-sm">
                <span className="font-bold">{hotel.total_points?.toLocaleString()} pts</span>
                <span className="text-brand-ink/65">({hotel.points_per_night?.toLocaleString()}/night)</span>
              </div>
              <PointsBar used={hotel.total_points} total={chase} label="Chase UR" color="bg-indigo-500" />
            </div>
          )}

          {/* Points Summary */}
          <div className="bg-brand-ink/5 rounded-xl p-4 border border-brand-ink/5">
            <div className="text-xs font-bold text-brand-ink/65 uppercase mb-2">Total Points Cost</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-brand-ink/65">Amex MR:</span>{' '}
                <span className="font-bold">{(rec?.total_points_two_travelers || 0).toLocaleString()}</span>
                <span className="text-brand-ink/55"> / {amex.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-brand-ink/65">Chase UR:</span>{' '}
                <span className="font-bold">{(hotel?.total_points || 0).toLocaleString()}</span>
                <span className="text-brand-ink/55"> / {chase.toLocaleString()}</span>
              </div>
            </div>
            <div className="mt-2 text-xs text-teal">
              {((amex - (rec?.total_points_two_travelers || 0)) + (chase - (hotel?.total_points || 0))).toLocaleString()} points remaining after booking
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => { onClose(); navigate(`/destination/${dest.id}`); }}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-teal to-teal-dark text-navy rounded-xl text-sm font-bold hover:opacity-90 transition shadow-md shadow-teal/20"
            >
              Full Details &rarr;
            </button>
            <button
              onClick={() => toggleCompare(dest.id)}
              className={`px-4 py-3 rounded-xl text-sm font-medium transition ${
                compareIds.includes(dest.id)
                  ? 'bg-gold/20 text-gold ring-1 ring-gold/30'
                  : 'bg-brand-ink/5 text-brand-ink/70 hover:bg-brand-ink/10'
              }`}
            >
              {compareIds.includes(dest.id) ? 'In Compare' : 'Compare'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
